import React, { useState } from 'react';
import { useStyletron } from 'baseui';
import { useAtomValue } from 'jotai';
import { LABELLING_TYPE } from '../../../../common/constant/constant';
import type { ShowNotificationT } from '../../../../common/types/types';
import { frameCountAtom } from '../../../labelling-v2/atoms/annotation-atoms';
import { fileTypeAtom } from '../../../labelling-v2/atoms/file-atoms';
import { isVideoLabelingM4EnabledAtom } from '../../../labelling-v2/atoms/m4-atoms';
import { selectedVideoIndexAtom } from '../../../labelling-v2/atoms/multi-video-atoms';
import { isLoadingAtom } from '../../../labelling-v2/atoms/tool-atoms';
import { FrameTimelineContainer } from '../../../labelling-v2/components/frame-timeline-v4/frame-timeline-container';
import type { BoundingBoxTrackingInputT, BoundingBoxTrackingOutputT, SAMSegmentVideoData } from '../../../types';
import { useMediaStateIsFramesLoading, useMediaStateUpdateFrameLoadStatus } from '../../providers/hooks/use-media-state';
import { AnnotationTimelineV2 } from './frames-timeline/annotation-timeline-v2';
import { getColumnHeaderHeight, ROW_HEADER_WIDTH } from './frames-timeline/annotation-timeline-v2/constants';
import { FRAME_CONTROL_MODE } from './constants';
import { FrameControlPanelLayout } from './frame-control-panel-layout';
import { FramesControlsContainer } from './frame-controls-container';
import { useHandleFrameChange } from './use-handle-frame-change';
type PropsT = {
  onFrameHover: (frameIndex: number | null) => void;
  isVideoPlayer?: boolean;
  fetchAnnotationTrackingData?: (labelTitle: string, frameRange: {
    start: number;
    end: number;
  }) => Promise<SAMSegmentVideoData | Error>;
  getBoundingBoxTrackingData?: (frameIndex: number, bbox: BoundingBoxTrackingInputT['bbox'], numFramesToTrack: number) => Promise<BoundingBoxTrackingOutputT | Error>;
  showNotification?: ShowNotificationT;
};
const FramesControlPanel = ({
  onFrameHover,
  isVideoPlayer,
  fetchAnnotationTrackingData,
  getBoundingBoxTrackingData,
  showNotification
}: PropsT) => {
  const fileType = useAtomValue(fileTypeAtom);
  const isLoading = useAtomValue(isLoadingAtom);
  const totalFrames = useAtomValue(frameCountAtom);
  const [css, theme] = useStyletron();
  const selectedVideoIndex = useAtomValue(selectedVideoIndexAtom);
  const isMultiVideoIndex = selectedVideoIndex !== 0;
  const isVideoLabelingM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const updateFrameLoadStatus = useMediaStateUpdateFrameLoadStatus();
  const isFramesLoading = useMediaStateIsFramesLoading();
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const handleFrameChange = useHandleFrameChange();
  const mode = fileType === LABELLING_TYPE.PDF ? FRAME_CONTROL_MODE.COMPACT : FRAME_CONTROL_MODE.NORMAL;
  const frameControls = <FramesControlsContainer onFrameChange={handleFrameChange} isDisabled={isLoading} mode={mode} isTimelineExpanded={timelineExpanded} onIsTimelineExpandedChange={setTimelineExpanded} isVideoPlayer={!!isVideoPlayer} />;
  const annotationTimeline = isVideoLabelingM4Enabled ? <FrameTimelineContainer totalFrames={totalFrames} /> : <AnnotationTimelineV2 totalFrames={totalFrames} onHoveredFrameChange={onFrameHover} onFrameClick={handleFrameChange} isExpanded={timelineExpanded} showNotification={showNotification} fetchAnnotationTrackingData={fetchAnnotationTrackingData} getBoundingBoxTrackingData={getBoundingBoxTrackingData} isDisabled={isLoading} />;
  return <FrameControlPanelLayout frameControls={frameControls} annotationTimeline={isMultiVideoIndex ? <div className={css({
    position: 'relative',
    zIndex: 0,
    width: '100%',
    paddingTop: getColumnHeaderHeight(theme),
    paddingLeft: `${ROW_HEADER_WIDTH}px`,
    overflow: 'hidden',
    height: '100%'
  })}></div> : annotationTimeline} mode={mode} isFramesLoading={!!isFramesLoading} setImageLoadStatus={updateFrameLoadStatus} />;
};
export default React.memo(FramesControlPanel);